/**
 * Authentication API Service
 *
 * Handles OAuth flows, token management, and user authentication
 */

import { apiClient } from './client';
import type { User, AuthTokenResponse } from './types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || window.location.origin;

/**
 * Initiate Google OAuth login flow
 * Redirects user to backend OAuth endpoint
 */
export function initiateGoogleLogin(): void {
  const redirectUrl = `${API_BASE}/auth/google`;
  window.location.href = redirectUrl;
}

/**
 * Initiate Microsoft OAuth login flow
 * Redirects user to backend OAuth endpoint
 */
export function initiateMicrosoftLogin(): void {
  const redirectUrl = `${API_BASE}/auth/microsoft`;
  window.location.href = redirectUrl;
}

/**
 * Handle OAuth callback
 * Extracts JWT token from URL query parameter after OAuth redirect
 *
 * @returns token if found in URL, null otherwise
 */
export function handleAuthCallback(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  if (token) {
    // Store token in API client
    apiClient.setToken(token);

    // Clean up URL (remove token from query params)
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);

    return token;
  }

  return null;
}

/**
 * Get current authenticated user information
 * @returns User object or null if not authenticated
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    if (!apiClient.isAuthenticated()) {
      return null;
    }

    const user = await apiClient.get<User>('/auth/me');
    return user;
  } catch (error) {
    console.error('Failed to fetch current user:', error);
    // If token is invalid, clear it
    logout();
    return null;
  }
}

/**
 * Logout current user
 * Clears authentication token and redirects to home
 */
export function logout(): void {
  apiClient.setToken(null);
  // Optionally redirect to home page
  // window.location.href = '/';
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return apiClient.isAuthenticated();
}

/**
 * Get current auth token
 */
export function getAuthToken(): string | null {
  return apiClient.getToken();
}

/**
 * Manual token refresh (if needed)
 * Some implementations might have refresh token logic
 */
export async function refreshToken(): Promise<boolean> {
  try {
    const response = await apiClient.post<AuthTokenResponse>('/auth/refresh', {});
    apiClient.setToken(response.token);
    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    logout();
    return false;
  }
}
